import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import noResultsImageSrc from '../../../../../../../../src/assets/images/noResults.svg';
import {
  buildContactsManagerSyncsUrl,
  contactsLearnMoreUrl,
} from '../../../../../../../../src/helpers/contactsManagerHelpers';
import {globalNamespace} from '../../../../../../../../src/helpers/models/namespaceHelpers';
import {useAppSelector} from '../../../../../../../../src/helpers/reduxHelpers';
import {
  CrmSyncIntegrationResourceTypesEnum,
  CrmSyncSourcesEnum,
} from '../../../../../../../../src/models/api/crmSyncModel';
import {SyncJobModel} from '../../../../../../../../src/models/api/jobModel';
import {getCompanyHasCrmSyncFeature} from '../../../../../../../../src/store/data/company/companySelectors';
import {ContactsManagerPanelId} from '../../../../../../../../src/store/panels/panelStateBase';
import {Handler, HandlerOf, Renderer} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {CreateContactButtonContainer} from '../../common/buttons/createContactButtonContainer';
import {InfoButton} from '../../common/buttons/infoButton';
import {ContactsManagerImportPopover} from '../../common/contactsManagerImportPopover';
import {CrmSyncHeaderDetailsContainer} from '../../common/crmSyncHeaderDetailsContainer';
import {CompanyContactsInfoBoxes} from '../../common/infobox/companyContactsInfoBoxes';
import {VerticalRule} from '../../common/verticalRule';
import {useContactsManagerContext} from '../../contactsManagerContext';
import {ContactsManagerHeader} from '../../contactsManagerHeader';
import {ImportContactsCSVDialogContainer} from '../../dialogs/import/contacts/importContactsCSVDialogContainer';
import {ExportContactsPopoverContainer} from '../../exportContacts/exportContactsPopoverContainer';
import {buildInfoBoxToggleLabel} from '../../helpers/infoBoxHelpers';
import {ContactsTableMultiSelectActions} from '../../tables/common/contacts/contactsTableMultiSelectActions';
import {ContactsManagerTableEmptyState} from '../../tables/common/contactsManagerEmptyState';
import {ContactsTableContainer} from '../../tables/contacts/contactsTableContainer';

/*
 * Props.
 */

interface CompanyContactsPageProps {
  isShowingImportCSVDialog: boolean;
  hasRunningImportContactsCSVJobs: boolean;
  isInfoBoxExpanded: boolean;
  hasCrmSyncFullPageSalesforce: boolean;
  selectedCardIds: ReadonlyArray<number>;
  activeSyncJobs: ReadonlyArray<SyncJobModel>;
  onToggleInfoBox: Handler;
  onToggleImportCSVClick: Handler;
  navigateToLink: HandlerOf<string>;
}

/*
 * Styles.
 */

const StyledImg = styled.img`
  width: 92px;
`;

const StyledWrapperDiv = styled.div`
  display: flex;
  height: inherit;
`;

const StyledContextDetailsWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  flex-wrap: wrap;
`;

/*
 * Component.
 */

export const CompanyContactsPage: FC<CompanyContactsPageProps> = (props) => {
  const {
    selectedCardIds,
    isInfoBoxExpanded,
    hasCrmSyncFullPageSalesforce,
    hasRunningImportContactsCSVJobs,
    isShowingImportCSVDialog,
    activeSyncJobs,
    navigateToLink,
    onToggleInfoBox,
    onToggleImportCSVClick,
  } = props;
  const {panelId} = useContactsManagerContext();
  const hasCrmSyncFeature = useAppSelector(getCompanyHasCrmSyncFeature);

  const onImportSalesforceClick = () => {
    navigateToLink(buildContactsManagerSyncsUrl(CrmSyncSourcesEnum.SALESFORCE));
  };

  const renderContactsManagerImportPopover = () => (
    <ContactsManagerImportPopover
      isDisabled={hasRunningImportContactsCSVJobs}
      onImportCSVClick={onToggleImportCSVClick}
      hasCrmSyncFeature={hasCrmSyncFeature}
      onImportSalesforceClick={hasCrmSyncFullPageSalesforce ? onImportSalesforceClick : undefined}
    />
  );

  return (
    <>
      <ContactsManagerHeader
        title={<Trans id="company-contacts-table-title">Company contacts</Trans>}
        contextDetails={maybeRenderContextDetails(panelId, hasCrmSyncFullPageSalesforce, activeSyncJobs)}
        actions={
          <>
            <ExportContactsPopoverContainer />
            <CreateContactButtonContainer />
            {renderContactsManagerImportPopover()}
            <VerticalRule />
            <InfoButton
              label={buildInfoBoxToggleLabel()}
              onToggle={onToggleInfoBox}
              isActive={isInfoBoxExpanded}
            />
          </>
        }
      />
      <StyledWrapperDiv>
        <ContactsTableContainer
          panelId={panelId}
          renderEmpty={() => renderEmptyState(renderContactsManagerImportPopover)}
          queryOptions={{namespace: globalNamespace}}
          multiSelectHeaderActions={
            <ContactsTableMultiSelectActions isAddToListsButtonVisible cardIds={selectedCardIds} />
          }
        />
        {isInfoBoxExpanded && <CompanyContactsInfoBoxes onRequestClose={onToggleInfoBox} />}
      </StyledWrapperDiv>
      {isShowingImportCSVDialog && (
        <ImportContactsCSVDialogContainer panelId={panelId} onRequestClose={onToggleImportCSVClick} />
      )}
    </>
  );
};

/*
 * Helpers.
 */

function maybeRenderContextDetails(
  panelId: ContactsManagerPanelId,
  hasCrmSyncFullPageSalesforce: boolean,
  activeSyncJobs: ReadonlyArray<SyncJobModel>,
) {
  if (!hasCrmSyncFullPageSalesforce) {
    return null;
  }

  return (
    <StyledContextDetailsWrapperDiv>
      <CrmSyncHeaderDetailsContainer
        resourceType={CrmSyncIntegrationResourceTypesEnum.CARD}
        panelId={panelId}
        activeSyncJobs={activeSyncJobs}
      />
    </StyledContextDetailsWrapperDiv>
  );
}

function renderEmptyState(renderContactsManagerImportPopover: Renderer) {
  return (
    <ContactsManagerTableEmptyState
      title={<Trans id="company-contacts-table-empty-state-title">No company contacts</Trans>}
      image={<StyledImg alt="company-contacts-table-empty-state-image" src={noResultsImageSrc} />}
      description={
        <Trans id="company-contacts-table-empty-state-description">
          Company Contacts allow you to manage contact information that is shared across your entire company
          within Front.
        </Trans>
      }
      learnMoreUrl={contactsLearnMoreUrl}
    >
      <CreateContactButtonContainer />
      {renderContactsManagerImportPopover()}
    </ContactsManagerTableEmptyState>
  );
}
